/**
 * Task D1 — every SIM activation and SIM swap this account has run, with
 * filters on type, status and date (route 21, `GET /jobs`).
 *
 * **Those two families are the whole screen.** The account's upstream history
 * also holds whitelisting, disconnects, temp-disconnects and plan changes;
 * `GET /jobs` lists and counts none of them (`modules/jobs/jobTypes.ts`), so
 * neither the header, the empty state nor the type control may imply otherwise
 * — a list that promises everything and shows two families is read as "my
 * submission was never recorded".
 *
 * **Every status on this screen is rendered by `JobStatusBadge`.** The list is
 * the surface most likely to tempt someone into a quick inline `status ===
 * 'FAILED' ? 'red' : 'green'`, and that shortcut is exactly how `EXPIRED` comes
 * to read as a failure in one place and not another (criterion 18).
 *
 * Filter state lives in the **URL**, next to `page` and `pageSize`, for the same
 * reason `usePaginatedQuery` puts pagination there: "the failed activations from
 * Tuesday" has to survive being pasted into a support conversation. Changing a
 * filter drops `page`, because page 4 of a different result set is a different
 * thing that happens to have the same number.
 */

import type { ClientJob, JobStatus, JobType } from '@rd/shared';
import { RefreshCw } from 'lucide-react';
import { useCallback, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider.js';
import { JOB_TYPE_FEATURE } from '../../auth/keys.js';
import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ErrorState } from '../../components/ErrorState.js';
import { Field } from '../../components/Field.js';
import { Input, Select } from '../../components/Input.js';
import { PageHeader } from '../../components/PageHeader.js';
import { Pagination } from '../../components/Pagination.js';
import { Table, type Column } from '../../components/Table.js';
import { usePaginatedQuery } from '../../lib/paginated.js';
import { JOB_POLL_INTERVAL_MS } from '../../lib/useJob.js';

import { JobStatusBadge } from './JobStatusBadge.js';

/**
 * A label for every job type in the contract.
 *
 * `Record<JobType, string>` rather than a partial map: a job family added to
 * `@rd/shared` without a label here is a **compile error**, not a table cell
 * showing `SMS_SEND_BATCH` to an operator.
 */
const JOB_TYPE_LABEL: Record<JobType, string> = {
  SIM_ACTIVATE: 'Activate',
  SIM_ACTIVATE_TEST: 'Activate in test mode',
  SIM_ACTIVATE_FROM_TEST: 'Move to a live plan',
  SIM_SWAP: 'Swap SIM',
  SIM_SUSPEND: 'Put in safe custody',
  SIM_RESUME: 'Take out of safe custody',
  SIM_TEMP_DISCONNECT: 'Disconnect',
  SIM_TD_RESUME: 'Reconnect',
  SIM_CHANGE_PLAN: 'Change plan',
  KYC_BULK_UPLOAD: 'KYC bulk upload',
  ARP_BULK_UPLOAD: 'ARP bulk upload',
  LOCATION_WHITELIST: 'Location whitelist',
  SMS_SEND: 'Send SMS',
  SMS_SEND_CUSTOM: 'Send custom SMS',
  SMS_SEND_BATCH: 'Send SMS batch',
  SMS_WHITELIST_BATCH: 'SMS whitelist batch',
  VOICE_WHITELIST_UPDATE: 'Voice whitelist update',
};

/**
 * The job families this screen can show — the server's own vocabulary
 * (`GET /jobs`, documented as an enum in `openapi-ours.yaml`), transcribed.
 *
 * Two guarantees, both from the compiler: every entry is a real `JobType`, so
 * it has a label above; and every entry has an entitlement key, so the option
 * can be hidden from an account that cannot start that kind of work. A type
 * outside this list is a `400` from the API rather than an empty page, which
 * is why the control must not offer one — `apiContract.spec.ts` checks this
 * list against the contract document so the two cannot drift.
 */
type FilterableJobType = JobType & keyof typeof JOB_TYPE_FEATURE;
export const TYPE_FILTERS = [
  'SIM_ACTIVATE',
  'SIM_SWAP',
] as const satisfies readonly FilterableJobType[];

/**
 * The status filter's options, matching the enum `GET /jobs` validates its
 * `status` parameter against (`apps/api/src/modules/jobs/jobs.schema.ts`).
 *
 * Unlike a SIM's status (§13.8, where the wire spelling is undocumented and the
 * options are built from what the response contained), a job status is **ours**
 * — it is a Prisma enum we defined — so listing it is transcription, not a
 * guess.
 */
const STATUS_FILTERS: readonly JobStatus[] = [
  'QUEUED',
  'SUBMITTED',
  'RUNNING',
  'SUCCEEDED',
  'PARTIAL',
  'FAILED',
  'EXPIRED',
];

function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleString();
}

export function JobListPage(): ReactElement {
  const { hasFeature } = useAuth();
  const [params, setParams] = useSearchParams();

  /**
   * The types this account is offered: the listed vocabulary narrowed to the
   * work the tenant can actually start.
   */
  const typeOptions = TYPE_FILTERS.filter((key) => hasFeature(JOB_TYPE_FEATURE[key]));

  /*
    Read through those options rather than straight out of the URL. Filter state
    lives in the URL precisely so it can be pasted into a support conversation,
    which means links minted while this control still offered nine families are
    still in circulation — and `?type=SIM_CHANGE_PLAN` is now a `400` from
    `GET /jobs`: an error state with no rows and no page controls, while the
    control itself reads "Anything", because a `<select>` given a value none of
    its options carry falls back to the first one. Nothing on the screen would
    say a filter caused it. So a value the control would not offer is not a
    filter this screen holds: the link degrades to the unfiltered list, and what
    the control shows is what was asked for.
  */
  const requestedType = params.get('type') ?? '';
  const type = typeOptions.some((key) => key === requestedType) ? requestedType : '';
  const status = params.get('status') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  const setFilter = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value === '') next.delete(key);
      else next.set(key, value);
      // A new filter is a new result set. Keeping `page=4` would land the user
      // on an empty page and look like "there are no jobs".
      next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const query = usePaginatedQuery<ClientJob>({
    key: ['jobs'],
    path: '/jobs',
    // Only the filters the user actually set are sent. An empty string is not a
    // filter value; it is the absence of one, and `status=` would be a
    // validation failure rather than "all statuses".
    query: {
      ...(type === '' ? {} : { type }),
      ...(status === '' ? {} : { status }),
      ...(from === '' ? {} : { from }),
      ...(to === '' ? {} : { to }),
    },
    /**
     * The list polls itself while any row on the page is still running, at the
     * same cadence the drawer polls a single job — a list of "In progress"
     * rows that never move is what taught operators to hammer F5.
     *
     * **Keyed on `terminal`, never on a status list** (same rule as `useJob`):
     * an eighth status added next year stops the poll correctly here, whereas
     * a hand-copied enum would spin forever on it. All-terminal pages — and
     * pages we have no data for yet — do not poll; history does not change.
     */
    refetchInterval: (q) =>
      q.state.data?.data.some((job) => !job.terminal) ? JOB_POLL_INTERVAL_MS : false,
  });

  const columns: Column<ClientJob>[] = [
    {
      key: 'ref',
      header: 'Reference',
      // `kind: 'mono'` rather than a hand-written `font-mono` on the link: a
      // job ref is an identifier read character by character exactly like an
      // ICCID, so it gets the estate table's identifier treatment — tabular
      // figures, `--tracking-ident`, no wrapping — from the one place that
      // defines it.
      kind: 'mono',
      render: (job) => (
        // Underlined on hover, not permanently. A rule under all 25 refs turns
        // the column into a block of underscored text and makes the 26-character
        // ULID harder to read, which is the opposite of what the column is for.
        <Link
          to={`/jobs/${job.ref}`}
          className="rounded-[var(--radius-sm)] text-primary underline-offset-[3px] hover:underline focus-visible:underline"
        >
          {job.ref}
        </Link>
      ),
    },
    {
      key: 'type',
      header: 'What ran',
      render: (job) => (
        <span className="whitespace-nowrap font-medium text-body">{JOB_TYPE_LABEL[job.type]}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      // A width, so the pill and its sentence have a column of their own
      // instead of stealing whatever the counts do not use — which made the
      // numeric columns move as the statuses on the page changed.
      width: '15rem',
      // Criterion 18: through the badge, never a local mapping.
      render: (job) => <JobStatusBadge job={job} compact />,
    },
    {
      key: 'total',
      header: 'SIMs',
      kind: 'numeric',
      width: '5rem',
      render: (job) => job.counts.total,
    },
    {
      key: 'succeeded',
      header: 'Succeeded',
      kind: 'numeric',
      width: '6.5rem',
      render: (job) => job.counts.succeeded,
    },
    {
      key: 'attention',
      header: 'Need attention',
      kind: 'numeric',
      width: '8rem',
      render: (job) => job.counts.failed + job.counts.rejected,
    },
    {
      key: 'submittedAt',
      header: 'Submitted',
      render: (job) => (
        <span className="whitespace-nowrap tabular-nums text-muted">
          {formatWhen(job.submittedAt)}
        </span>
      ),
    },
  ];

  /**
   * Real freshness, same instrument as the dashboard: the timestamp is the
   * query's own, so it cannot claim to be newer than the data it sits beside.
   */
  const fetchedAt = query.dataUpdatedAt > 0 ? new Date(query.dataUpdatedAt) : null;

  return (
    <>
      <PageHeader
        title="Jobs"
        description="Every SIM activation and SIM swap this account has run, and how it went."
        actions={
          <div className="flex items-center gap-[var(--space-3)]">
            {fetchedAt ? (
              <p className="text-[length:var(--text-2xs)] tabular-nums text-muted">
                as of {fetchedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            ) : null}
            <Button variant="ghost" loading={query.isFetching} onClick={() => void query.refetch()}>
              <RefreshCw aria-hidden="true" size={14} strokeWidth={2} />
              Refresh
            </Button>
          </div>
        }
      />

      {/*
        One framed control group, the same object as the estate's filter bar:
        everything that changes what the table below shows lives inside one
        edge. Four bare fields on the canvas read as four unrelated decisions,
        and on a phone `grid-cols-4` gave each of them about 80px.
      */}
      <section
        aria-label="Filters"
        className="card mb-[var(--space-4)] p-[var(--space-3)] shadow-[var(--elevation-0)]"
      >
        <div className="flex flex-wrap items-end gap-[var(--space-3)]">
          <div className="min-w-0 flex-1 basis-full sm:basis-[12rem]">
            <Field label="What ran">
              {(field) => (
                <Select
                  id={field.id}
                  value={type}
                  onChange={(event) => setFilter('type', event.target.value)}
                >
                  <option value="">Anything</option>
                  {typeOptions.map((key) => (
                    <option key={key} value={key}>
                      {JOB_TYPE_LABEL[key]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <div className="min-w-0 flex-1 basis-full sm:basis-[10rem]">
            <Field label="Status">
              {(field) => (
                <Select
                  id={field.id}
                  value={status}
                  onChange={(event) => setFilter('status', event.target.value)}
                >
                  <option value="">Any status</option>
                  {STATUS_FILTERS.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <div className="min-w-0 flex-1 basis-full sm:basis-[9.5rem]">
            <Field label="From">
              {(field) => (
                <Input
                  id={field.id}
                  type="date"
                  value={from}
                  onChange={(event) => setFilter('from', event.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="min-w-0 flex-1 basis-full sm:basis-[9.5rem]">
            <Field label="To">
              {(field) => (
                <Input
                  id={field.id}
                  type="date"
                  value={to}
                  onChange={(event) => setFilter('to', event.target.value)}
                />
              )}
            </Field>
          </div>
        </div>
      </section>

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : null}

      {/*
        The table renders while the request is in flight rather than a stack of
        loose grey bars in its place. It is the same decision the estate list
        already makes and the reason `Table` has a `loading` prop at all: the
        header stays, so the columns still explain what is coming, and the
        skeleton cells are the widths of the real columns, so nothing moves when
        the rows land. Pagination stays behind `query.data`, because a page
        control with no meta to drive it would be inventing a range.
      */}
      {query.data || query.isPending ? (
        <>
          <Table
            caption="Jobs, newest first"
            columns={columns}
            rows={query.data?.data ?? []}
            rowKey={(job) => job.ref}
            loading={query.isPending}
            empty={
              <EmptyState
                title="No activations or swaps yet"
                body="Activations and SIM swaps appear here as you run them. Other work this account runs — plan changes, whitelisting, disconnections — is not listed on this screen."
              />
            }
          />
          {query.data ? (
            <Pagination meta={query.data.meta} onPageChange={query.setPage} label="Jobs" />
          ) : null}
        </>
      ) : null}
    </>
  );
}
